//
import React, { useLayoutEffect, useRef, useState } from 'react';

import DialogContent from '@mui/material/DialogContent';
import type { DialogContentProps } from '@mui/material/DialogContent';
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

import ErrorBoundary from 'components/ErrorBoundary';

export type StdDialogContentProps = Omit<DialogContentProps, 'sx'> & {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  dialogId?: string;
  sharedcomponentid?: string;
};

/**
 * StdDialogContent should be used instead of the MUI DialogContent. StdDialogContent is a thin wrapper whose primary purpose
 * is to add scrolling support if content is larger than height constraint assigned by parent. In the case where scrollable
 * content is available, a fixed divider will automatically be added at the bottom of the content.
 *
 * This component is meant to be used with StdDialog.
 */
const StdDialogContent: React.FC<StdDialogContentProps> = ({ children, sx: ferrySx, ...other }) => {
  const theme = useTheme();

  const [requiresScrolling, setRequiresScrolling] = useState(false);

  const contentRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const { current } = contentRef;
    if (!current || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (!current) {
          return;
        }
        const needsScrolling = current.scrollHeight > current.clientHeight;
        setRequiresScrolling((prev) => (prev !== needsScrolling ? needsScrolling : prev));
      });
    });
    observer.observe(current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <DialogContent
      ref={contentRef}
      sx={{
        boxSizing: 'border-box', // protect against borderBottom affecting size calculations and causing infinite resize loop
        borderBottom: `1px solid transparent`,
        borderBottomColor: requiresScrolling ? theme.palette.divider : 'transparent',
        ...ferrySx,
      }}
      {...other}
    >
      <ErrorBoundary>{children}</ErrorBoundary>
    </DialogContent>
  );
};

export default StdDialogContent;
